import React from "react";
import { DragSource, DropTarget } from "react-dnd";
import Icon from "../Icon/Icon";
import dndIcon from "../../svgs/grip-lines.svg";

const DND_ITEM_TYPE = 'row';

const BodyRow = React.forwardRef(function BodyRow({ rowKey, index, children, connectDragSource, connectDragPreview, connectDropTarget }, ref) {
    const dragRef = React.useRef(null);
    const dropRef = React.useRef(null);
    connectDragSource(dragRef);
    connectDragPreview(dropRef);
    connectDropTarget(dropRef);
    React.useImperativeHandle(ref, () => ({
        getNode: () => dragRef.current,
    }));
    return (
        <tr ref={dropRef} key={rowKey} className={rowKey}>
            <td ref={dragRef} style={{cursor: 'move', padding: '3px 0'}}>
                <Icon svg={dndIcon}/>
            </td>
            {children}
        </tr>
    );
});

const rowSource = {
    beginDrag(props) {
        return {
            index: props.index,
        };
    },
    endDrag(props) {
        props.moveCorrespondingRows();
    }
};

const rowTarget = {
    hover(props, monitor, component) {
        if (!component) return null;
        // node = HTML Div element from imperative API
        const node = component.getNode();
        if (!node) return null;

        const dragIndex = monitor.getItem().index;
        const hoverIndex = props.index;
        // Don't replace items with themselves
        if (dragIndex === hoverIndex) return;

        // Determine rectangle on screen
        const hoverBoundingRect = node.getBoundingClientRect();
        // Get vertical middle
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        // Determine mouse position
        const clientOffset = monitor.getClientOffset();
        // Get pixels to the top
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;
        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%
        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        // Dragging upwards
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

        // Time to actually perform the action
        props.moveRow(dragIndex, hoverIndex);

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations, but it's good here for the sake of performance to avoid expensive index searches.
        monitor.getItem().index = hoverIndex;
    },
};

const DraggableBodyRow =
    DropTarget(DND_ITEM_TYPE, rowTarget, (connect, monitor) => ({
        connectDropTarget: connect.dropTarget(),
        isOver: monitor.isOver(),
    }))(
    DragSource(DND_ITEM_TYPE, rowSource, (connect, monitor) => ({
        connectDragSource: connect.dragSource(),
        connectDragPreview: connect.dragPreview(),
        isDragging: monitor.isDragging(),
    }))(BodyRow)
);

export default DraggableBodyRow;